// src/App.js
import React, { useState, useEffect, useRef } from 'react';
import './axiosIntercept';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation
} from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import AuthForm from './components/AuthForm';
import BoardList from './components/BoardList';
import BoardView from './components/BoardView';
import BoardEdit from './components/BoardEdit';
import UserProfile from './components/UserProfile';
import AboutUs from './components/AboutUs';
import ProtectedRoute from './components/ProtectedRoute';
import OrgProfile from './components/OrgProfile';
import Layout from './components/Layout';
import BoardFolderList from './components/BoardFolderList';
import AdminTools from './components/AdminPge';
import AdminEmail from './components/AdminEmail';
import TutorialBoardView from './components/TutorialBoardView';
import TutorialPage from './components/TutorialPage';
import Community from './components/Community';
import PresetBoards from './components/PresetBoards';
import SmartBoardSetup from './components/SmartBoardSetup';
import { ConfirmProvider } from './components/ConfirmPopup';
import { fixViewportHeight } from './services/fixViewport';
import {
  syncTokenWithServer,
  isAccessExpired,
  getAccountType,
  isTokenExpired,
  forceLogout,
  forceUserProfile
} from './context/accountUtils';
import PaymentSuccess from './components/PaymentSuccess';
import PaymentCancel from './components/PaymentCancel';

import './App.css';


/* ----------------------- NetGuard Modal ----------------------- */
function NetGuardModal({ open, title, message, onRetry, onClose, onIgnore }) {
  if (!open) return null;
  if (onIgnore === 'ignore') { return null; }

  return (
    <div style={styles.backdrop}>
      <div style={styles.modal}>
        <h3 style={{ margin: 0, marginBottom: 8 }}>{title}</h3>
        <p style={{ marginTop: 0 }}>{message}</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={styles.btnSecondary}>Dismiss</button>
          <button onClick={onRetry} style={styles.btnPrimary}>Retry</button>
        </div>
      </div>
    </div>
  );
}
/* -------------------------------------------------------------- */

function App() {
  const [netOpen, setNetOpen] = useState(false);
  const [netReason, setNetReason] = useState('empty');
  const watchdog = useRef(null);
  const hydrated = useRef(false);

useEffect(() => {
  // Only run inside Android WebView that exposes the bridge
  if (!window.Android) return;

  (async () => {
    try {
      const hasSW =
        !!navigator.serviceWorker &&
        (await navigator.serviceWorker.getRegistrations()).length > 0;

      // Try to match your app shell; adjust keys if needed
      const cachedRoot = await caches.match('/aac/') || await caches.match('/aac/index.html');

      const ready = !!(hasSW && cachedRoot);
      // Tell the native layer whether we can run fully offline
      if (typeof window.Android.setPwaCacheReady === 'function') {
        window.Android.setPwaCacheReady(ready);
      } else if (typeof window.Android.reportCacheReady === 'function') {
        // backward-compat if you used reportCacheReady earlier
        window.Android.reportCacheReady(ready);
      }
    } catch {
      if (typeof window.Android.setPwaCacheReady === 'function') {
        window.Android.setPwaCacheReady(false);
      } else if (typeof window.Android.reportCacheReady === 'function') {
        window.Android.reportCacheReady(false);
      }
    }
  })();
}, []);


  useEffect(() => {
    window.__markAppHydrated = () => {
      hydrated.current = true;
      if (watchdog.current) {
        clearTimeout(watchdog.current);
        watchdog.current = null;
      }
    };

    const onErr = (e) => {
      const r = e?.detail?.reason || 'server';
      setNetReason(r);
      setNetOpen(true);
    };
    window.addEventListener('netguard:error', onErr);

    watchdog.current = setTimeout(() => {
      if (!hydrated.current) {
        const r = (typeof navigator !== 'undefined' && !navigator.onLine) ? 'offline' : 'empty';
        setNetReason(r);
        setNetOpen(true);
      }
    }, 6000);

    return () => {
      window.removeEventListener('netguard:error', onErr);
      if (watchdog.current) clearTimeout(watchdog.current);
    };
  }, []);

  const handleRetry = () => {
    try {
      if (window.Android?.retry) {
        window.location.href = '/aac/boards';
      } else {
        window.location.href = '/aac/boards';
      }
    } catch {
      window.location.href = '/aac/boards';
    }
  };

  return (
    <AuthProvider>
      <ConfirmProvider>
        <BrowserRouter basename="/aac">
          <AppRoutes
            onHydrated={() => window.__markAppHydrated?.()}
            netOpen={netOpen}
            setNetOpen={setNetOpen}
            netReason={netReason}
            onRetry={handleRetry}
          />
        </BrowserRouter>
      </ConfirmProvider>
    </AuthProvider>
  );
}

function AppRoutes({ onHydrated, netOpen, setNetOpen, netReason, onRetry }) {
  const { user, loading } = useAuth();
  const [editMode, setEditMode] = useState(false);
  const [showSentenceBuilder, setShowSentenceBuilder] = useState(false);
  const [sentenceTiles, setSentenceTiles] = useState([]);
  const [initializing, setInitializing] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const PUBLIC_PATHS = [
    '/login',
    '/signup',
    '/signup-code',
    '/forgot-password',
    '/reset-password'
  ];

  // exact match or any subpath under those public routes
  const normalize = (p) => p.replace(/\/+$/, '');
  const path = normalize(location.pathname);
  const isPublic = PUBLIC_PATHS.map(normalize).some(p => path === p || path.startsWith(p + '/'));

  // If you still want an exact-match boolean:
  const isPublicRoute = PUBLIC_PATHS.includes(path);

  const params = new URLSearchParams(window.location.search);

  const isProfileExpired = path === '/profile' && params.get('upgrade') === '1';

  // Context-aware message
  let ctx = 'general';
  if (location.pathname.startsWith('/boards')) ctx = 'boardList';
  else if (location.pathname.startsWith('/board/')) ctx = 'boardView';
  else if (location.pathname.startsWith('/profile')) ctx = 'profile';
  else if (isPublic) ctx = 'ignore';

  let onIgnore = ctx;

  const title = {
    offline: "You're offline",
    timeout: 'Server is taking too long',
    server: "Can't reach the server",
    empty: 'Nothing loaded yet',
  }[netReason] || 'Notice';

  let message = {
    offline:
      "You seem to be offline, some features may not be available in offline mode. Please reconnect to the internet and try again.",
    timeout:
      "We aren't getting a response from the server right now. It might be temporary—please try again.",
    server:
      "We couldn't load data from the server. If your internet works, the server may be busy. Please try again.",
    empty:
      "No content has appeared yet. This can happen if the device is offline or the server is slow.",
  }[netReason] || '';

  if (netReason === 'offline') {
    if (ctx === 'boardList') {
      message =
        "You seem to be offline, some features within this page are not be available in offline mode. To save/move/delete boards or folders, please reconnect to the internet and try again.";
    } else if (ctx === 'boardView') {
      message =
        "You seem to be offline, and this board has not yet been opened while online (cached). To use this board, please reconnect to the internet and try again.";
    } else if (ctx === 'profile') {
      message = "You seem to be offline, any profile changes can't be saved while offline. Please reconnect to the internet.";
    }
  }

  useEffect(() => {
    if (isPublic) {
      setInitializing(false);
      return;
    }

    const init = async () => {

      if (!user) {
        setInitializing(false);
        return;
      }

      if (isTokenExpired() && !isPublic) {
        forceLogout();
        return;
      }
      // console.log('[GUARD]0', { path, isPublic, isProfileExpired, expired: isAccessExpired() });
      await syncTokenWithServer();
      // console.log('[GUARD]1', { path, isPublic, isProfileExpired, expired: isAccessExpired() });
      if (isAccessExpired() && !isPublic && isProfileExpired) {
        // forceLogout();
        // console.log('0Redirecting to profile due to access expiry');
        forceUserProfile();
        return;
      }
      setInitializing(false);
    };

    init();

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        if (isTokenExpired() && !isPublic) {
          forceLogout();
          return;
        }
        // console.log('[GUARD]02', { path, isPublic, isProfileExpired, expired: isAccessExpired() });
        await syncTokenWithServer();
        // console.log('[GUARD]2', { path, isPublic, isProfileExpired, expired: isAccessExpired() });
          if (isAccessExpired() && !isPublic && isProfileExpired) {
            // forceLogout();
            // console.log('1Redirecting to profile due to access expiry');
            forceUserProfile();
            return;
          }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    fixViewportHeight();
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [navigate, location.pathname, isPublic, isPublicRoute, user]);

  const didHydrateRef = React.useRef(false);
  useEffect(() => {
    if (!loading && !initializing && !didHydrateRef.current) {
      onHydrated?.();
      didHydrateRef.current = true;
    }
  }, [loading, initializing, onHydrated]);

  const toggleEditMode = () => setEditMode(prev => !prev);
  const toggleSentenceBuilder = () => setShowSentenceBuilder(prev => !prev);

  if (loading || initializing) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <>
      <Routes>
        <Route
          path="/login"
          element={
            user && !isTokenExpired() && !isAccessExpired()
              ? <Navigate to="/boards" replace />
              : <AuthForm isLogin={true} />
          }
        />
        <Route
          path="/signup"
          element={
            user && !isTokenExpired() && !isAccessExpired()
              ? <Navigate to="/boards" replace />
              : <AuthForm isLogin={false} />
          }
        />
        <Route
          path="/forgot-password"
          element={
            user && !isTokenExpired() && !isAccessExpired()
              ? <Navigate to="/boards" replace />
              : <AuthForm isLogin={false} />
          }
        />
        <Route
          path="/signup-code"
          element={
            user && !isTokenExpired() && !isAccessExpired()
              ? <Navigate to="/boards" replace />
              : <AuthForm isLogin={false} />
          }
        />
        <Route
          path="/reset-password"
          element={
            user && !isTokenExpired() && !isAccessExpired()
              ? <Navigate to="/boards" replace />
              : <AuthForm isLogin={false} />
          }
        />
        {/* AppRoutes.jsx */}
        <Route 
          path="/tutorial" 
          element={
              <ProtectedRoute>
                <Layout>
                  <TutorialPage />
                </Layout>
              </ProtectedRoute>
          }
        />
        <Route 
          path="/tutorial/boardlist" 
          element={
              <ProtectedRoute>
                <Layout>
                  <TutorialPage forcedStage="boardlist" />
                </Layout>
              </ProtectedRoute>
          }
        />
        <Route 
          path="/tutorial/boardview" 
          element={
              <ProtectedRoute>
                <Layout>
                  <TutorialPage forcedStage="boardview" />
                </Layout>
              </ProtectedRoute>
          }
        />
        <Route 
          path="/tutorial/community" 
          element={
              <ProtectedRoute>
                <Layout>
                  <TutorialPage forcedStage="community" />
                </Layout>
              </ProtectedRoute>
          }
        />
        <Route
          path="/boards"
          element={
            <ProtectedRoute>
              <Layout onToggleEditMode={toggleEditMode} onToggleSentenceBuilder={toggleSentenceBuilder}>
                {getAccountType() === 'unlimited' ? <BoardFolderList /> : <BoardList />}
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Layout>
                <UserProfile />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/about"
          element={
            <ProtectedRoute>
              <Layout>
                <AboutUs />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admintools"
          element={
            <ProtectedRoute>
              <Layout>
                <AdminTools />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/adminemail"
          element={
            <ProtectedRoute>
              <Layout>
                <AdminEmail />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/community"
          element={
            <ProtectedRoute>
              <Layout>
                <Community />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/presets"
          element={
            <ProtectedRoute>
              <Layout>
                <PresetBoards />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/smart-setup"
          element={
            <ProtectedRoute>
              <Layout>
                <SmartBoardSetup onboarding={true} />
              </Layout>
            </ProtectedRoute>
            }
        />
        <Route path="/org" 
          element={
            <ProtectedRoute>
              <Layout>
                <OrgProfile/>
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route
          path="/board/:id"
          element={
            <ProtectedRoute>
              <Layout
                onToggleEditMode={toggleEditMode}
                onToggleSentenceBuilder={toggleSentenceBuilder}
                showSentenceBuilder={showSentenceBuilder}
              >
                <BoardView
                  editMode={editMode}
                  showSentenceBuilder={showSentenceBuilder}
                  sentenceTiles={sentenceTiles}
                  setSentenceTiles={setSentenceTiles}
                />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/board/:id/edit"
          element={
            <ProtectedRoute>
              <Layout onToggleEditMode={toggleEditMode} onToggleSentenceBuilder={toggleSentenceBuilder}>
                <BoardEdit />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout onToggleEditMode={toggleEditMode} onToggleSentenceBuilder={toggleSentenceBuilder}>
                {getAccountType() === 'unlimited' ? <BoardFolderList /> : <BoardList />}
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment-success"
          element={
            <ProtectedRoute>
              <PaymentSuccess />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment-cancel"
          element={
            <ProtectedRoute>
              <PaymentCancel />
            </ProtectedRoute>
          }
        />
      </Routes>

      {/* ✅ Modal now inside Router */}
      <NetGuardModal
        open={netOpen}
        title={title}
        message={message}
        onRetry={onRetry}
        onClose={() => setNetOpen(false)}
        onIgnore={onIgnore}
      />
    </>
  );
}

export default App;

/* ---------- tiny inline styles for the modal ---------- */
const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  modal: {
    width: 360,
    maxWidth: '90vw',
    background: '#fff',
    borderRadius: 12,
    padding: 16,
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
    fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
  },
  btnPrimary: {
    padding: '8px 12px',
    borderRadius: 8,
    border: 'none',
    cursor: 'pointer',
    background: '#0ea5a3',
    color: '#fff',
    fontWeight: 600,
  },
  btnSecondary: {
    padding: '8px 12px',
    borderRadius: 8,
    border: '1px solid #ccc',
    background: '#fff',
    cursor: 'pointer',
  },
};
